import { z } from "zod";
import { SharedApiError, normalizeApiBaseUrl, type FetchLike } from "./shared";

export const profileAvatarSuccessSchema = z.object({
  ok: z.literal(true),
  avatar_url: z.string().nullable(),
});

const profileAvatarErrorSchema = z.object({
  error: z.string(),
  details: z.unknown().optional(),
});

export type ProfileAvatarSuccess = z.infer<typeof profileAvatarSuccessSchema>;

async function parseApiError(response: Response): Promise<SharedApiError> {
  const rawBody =
    typeof response.text === "function"
      ? await response.text().catch(() => "")
      : "";
  let payload: unknown = null;
  if (rawBody) {
    try {
      payload = JSON.parse(rawBody);
    } catch {
      payload = null;
    }
  }
  const parsed = profileAvatarErrorSchema.safeParse(payload);
  if (parsed.success) {
    return new SharedApiError(
      parsed.data.error,
      response.status,
      parsed.data.details,
    );
  }
  return new SharedApiError(
    `Profile avatar request failed (HTTP ${response.status})`,
    response.status,
    rawBody ? { body: rawBody.slice(0, 300) } : undefined,
  );
}

export function profileAvatarPath(): string {
  return "/api/profile/avatar";
}

export async function uploadProfileAvatarRequest({
  apiBaseUrl,
  accessToken,
  formData,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  formData: FormData;
  fetchImpl?: FetchLike;
}): Promise<ProfileAvatarSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${profileAvatarPath()}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const payload = await response.json().catch(() => null);
  const parsed = profileAvatarSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid profile avatar response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export async function removeProfileAvatarRequest({
  apiBaseUrl,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  fetchImpl?: FetchLike;
}): Promise<ProfileAvatarSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${profileAvatarPath()}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );

  if (!response.ok) {
    throw await parseApiError(response);
  }

  const payload = await response.json().catch(() => null);
  const parsed = profileAvatarSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid profile avatar response", 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}
